import React, { useCallback, useEffect, useState } from 'react';
import { formatDetailedTimestamp, formatRelativeTime } from '../utils/fliqueDates';

const STATUS_TABS = [
  { value: 'pending', label: 'Pending' },
  { value: 'reviewed', label: 'Reviewed' },
  { value: 'dismissed', label: 'Dismissed' },
  { value: 'all', label: 'All' },
];

export default function AdminReportsPage() {
  const [statusFilter, setStatusFilter] = useState('pending');
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actioning, setActioning] = useState(null);

  const load = useCallback(async (filter) => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`/api/admin/reports?status=${filter}`, {
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || 'Failed to load reports');
      setReports(data.reports || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(statusFilter);
  }, [load, statusFilter]);

  async function updateStatus(reportId, newStatus) {
    setActioning(reportId);
    try {
      const res = await fetch(`/api/admin/reports/${reportId}`, {
        method: 'PATCH',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || 'Failed to update');
      setReports((prev) => prev.filter((r) => r.id !== reportId));
    } catch (e) {
      alert(e.message || 'Something went wrong');
    } finally {
      setActioning(null);
    }
  }

  async function deleteContent(reportId) {
    setActioning(`delete:${reportId}`);
    try {
      const res = await fetch(`/api/admin/reports/${reportId}/content`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) throw new Error(data.error || 'Failed to delete content');
      setReports((prev) => prev.filter((r) => r.id !== reportId));
    } catch (e) {
      alert(e.message || 'Something went wrong');
    } finally {
      setActioning(null);
    }
  }

  return (
    <div className="flique-admin-reports">
      <h1 className="flique-admin-reports-title">Reports</h1>
      <div className="flique-admin-reports-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.value}
            type="button"
            className={`flique-admin-tab${statusFilter === t.value ? ' flique-admin-tab--active' : ''}`}
            onClick={() => setStatusFilter(t.value)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="flique-admin-reports-empty">Loading…</p>
      ) : error ? (
        <p className="flique-admin-reports-error">{error}</p>
      ) : reports.length === 0 ? (
        <p className="flique-admin-reports-empty">No {statusFilter === 'all' ? '' : statusFilter + ' '}reports.</p>
      ) : (
        <ul className="flique-admin-reports-list">
          {reports.map((r) => (
            <li key={r.id} className="flique-admin-report-row">
              <div className="flique-admin-report-meta">
                <span className="flique-admin-report-type">{r.contentType}</span>
                <span className="flique-admin-report-reason">{r.reason}</span>
                <span className="flique-admin-report-status flique-admin-report-status--{r.status}">{r.status}</span>
              </div>
              <div className="flique-admin-report-ids">
                <span className="flique-admin-report-label">Content ID:</span>
                <code className="flique-admin-report-id">{r.contentId}</code>
              </div>
              {r.contentTitle ? (
                <div className="flique-admin-report-content-title">
                  {r.contentTitle}
                </div>
              ) : null}
              <div className="flique-admin-report-content">
                {r.contentText && String(r.contentText).trim()
                  ? r.contentText
                  : <span className="text-muted">Content unavailable.</span>}
              </div>
              <div className="flique-admin-report-reporter">
                Reported by <strong>@{r.reporter.username}</strong>
                <span className="flique-admin-report-time">
                  {' '}·{' '}
                  <time dateTime={r.createdAt} title={formatDetailedTimestamp(r.createdAt)}>
                    {formatRelativeTime(r.createdAt)}
                  </time>
                </span>
              </div>
              {r.status === 'pending' && (
                <div className="flique-admin-report-actions">
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-light"
                    disabled={actioning === r.id || actioning === `delete:${r.id}`}
                    onClick={() => updateStatus(r.id, 'reviewed')}
                  >
                    Mark reviewed
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-light"
                    disabled={actioning === r.id || actioning === `delete:${r.id}`}
                    onClick={() => updateStatus(r.id, 'dismissed')}
                  >
                    Dismiss
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-flique"
                    disabled={actioning === r.id || actioning === `delete:${r.id}`}
                    onClick={() => deleteContent(r.id)}
                  >
                    Delete {r.contentType}
                  </button>
                </div>
              )}
              {r.reviewedBy && (
                <div className="flique-admin-report-reviewed-by">
                  Actioned by <strong>@{r.reviewedBy}</strong>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
